import { useState, useEffect, useCallback, useId } from 'react';
import classNames from 'classnames';

import { useLocation } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { API, Storage, Auth } from 'aws-amplify';
import { FaFileDownload } from 'react-icons/fa';
import S3_CONFIG from '../../../config/s3.json';

import { styles } from './util';
import { LdsSpinner } from '../../Util/LoadingIcons';

const styling = makeStyles(styles());
const MODEL = 'AERMOD';


export const Results = () => {
    const [requestId, setRequestId] = useState(null);
    const [requestStatus, setRequestStatus] = useState(null);
    const [requestBody, setRequestBody] = useState(null);
    const [requestIdsList, setRequestIdsList] = useState([]);
    const [openRequestBodyTable, setOpenRequestBodyTable] = useState(false);
    const [imageResults, setImageResults] = useState([]);
    const [textResults, setTextResults] = useState([]);
    const [resultTarFile, setResultTarFile] = useState(null);
    const [delMsg, setDelMsg] = useState('');
    const [privatePrefix, setPrivatePrefix] = useState('jobs/*/');
    const classes = styling();
    const location = useLocation();
    
    function handleInputChange(e) {
        const target = e.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        if (name === 'requestId'){
            setRequestId(value);
            setDelMsg('');
        }
    }

    // Initial setup
    useEffect(() => {
        
        setOrgPrefix();
        // Get requestId from URL params if one exists
        if (location.search) {
            parseQueryParams();
        }
        // Load requests
        getRequests();
        // 
    }, []);

    const setOrgPrefix = useCallback(async () => {
        const user = await Auth.currentAuthenticatedUser();
        setPrivatePrefix(`${S3_CONFIG.PREFIX}/${user.attributes['custom:organization']}/`);
    }, []);

    // Update request data 
    useEffect(() => {
        if (requestId !== null) {
            getRequestData(requestId);
            listS3Results(requestId);
            getTarFile(requestId);
        }

    }, [requestId, privatePrefix]);

    const parseQueryParams = useCallback(() => {
        const query = new URLSearchParams(location.search);
        for (const param of query.entries()) {
            if (requestId !== param[1]) {
                setRequestId(param[1]);
            }
        }
    }, [requestId, location.search]);

    const getRequests = useCallback(async () => {
        const options = {
            headers: {},
            response: true,
            queryStringParameters: {
                requestType: MODEL,
                callType: 'previousProjAll',
            }
        };

        try {
            const response = await API.get("AQcast", "/request", options)
            if (response.status >= 200 && response.status <= 299) {
                const data = response.data;
                const requests = data.requestIds;
                setRequestIdsList(requests);
                if (requestId === null && requests.length) {
                    setRequestId(requests[0].requestId);
                }
            } else {
                console.log('Bad response fetching requests');
                setRequestIdsList([]);
            }
        } catch(err) {
            console.log(`Error fetching ${MODEL} requests`);
            console.log(err);
        }
    }, [requestId]);

    const getRequestData = useCallback(async (id) => {
        const options = {
            headers: {},
            response: true,
            queryStringParameters: {
                requestId: id,
            }
        };

        try {
            const response = await API.get("AQcast", `/request/${id}`, options);
            if (response.status >= 200 && response.status <= 299) {
                const data = response.data;;
                setRequestBody(JSON.parse(data.request));
                setRequestStatus(data.status);
            } else {
                console.log(`Bad response fetching data for requestId ${id}`);
            }
            
        } catch(err) {
            console.log(`Error getting request data for requestId ${id}`);
            console.log(err);
        }
    }, [requestId]);

    const deleteRequestData = useCallback(async (id) => {
        const options = {
            headers: {},
            response: true,
            queryStringParameters: {
                requestId: id,
            }
        };

        try {
            const response = await API.del("AQcast", `/request/${id}`, options);
            if (response.status >= 200 && response.status <= 299) {
                const data = response.data;
                setDelMsg(data.message);
            } else {
                console.log(`Bad reesponse when trying to delete requestId ${id}`);
            }
            
        } catch(err) {
            console.log(`Error deleting request data for requestId ${id}`);
            console.log(err);
        }
    }, [requestId]);

    const loadFiles = useCallback(async (resultImages, resultText) => {
        const options =  {
            customPrefix: {private: privatePrefix},
            level: 'private',
        };

        setImageResults([]);
        setTextResults([]);

        // Fetch text
        for (const file of resultText) {
            try {
                const result = await Storage.get(file.key, {...options, download: true});
                const text = await result.Body.text();
                setTextResults( textResults => [...textResults, text]);
            } catch (err) {
                console.warn(`Error fetching text for file ${file.key}`);
                console.log(err);
            }
        }

        // Fetch images
        for (const file of resultImages) {
            try {
                const result = await Storage.get(file.key, { ...options, download: true});
                const imageURL = URL.createObjectURL(result.Body)
                setImageResults( imageResults => [...imageResults, imageURL])
            } catch (err) {
                console.warn(`Error fetching image for file ${file.key}`);
                console.log(err);
            }
        }

    }, [privatePrefix]);

    const listS3Results = useCallback(async (id) => {
        let options =  {
            customPrefix: {private: privatePrefix},
            level: 'private',
        };
        try {
            const result = await Storage.list(`${id}/UI`, options);
            const filteredResult = result.results.filter(file => file.label = file.key.split('/').pop());
            const resultImages = filteredResult.filter(file => file.label.endsWith('.png'));
            const resultText = filteredResult.filter(file => file.label.endsWith('.txt'));

            loadFiles(resultImages, resultText);
        } catch(err) {
            console.log('Error getting S3 results');
            console.log(err);
        }
    }, [privatePrefix]);

    const getTarFile = useCallback(async (id) => {
        let options = {
            customPrefix: {private: privatePrefix},
            level: 'private',
        };
        setResultTarFile('');
        try {
            const listResult = await Storage.list(`${id}/tar`, options);
            const filteredResults = listResult.results.filter(file => file.label = file.key.split('/').pop());
            const resultTarFiles = filteredResults.filter(file => file.label.endsWith('.tar.gz'));
            if (resultTarFiles.length) {
                const result = await Storage.get(resultTarFiles[0].key, options);
                setResultTarFile(result);
            }
        } catch(err) {
            console.log(`Error getting tar files for requestId ${id}`);
            console.log(err);
        }
    }, [privatePrefix]);

    const ResultImages = () => (
        <>
            {imageResults.map(image => 
                <img style={{textAlign:'right', maxWidth: '100%'}} key={useId()} src={image} alt="" />
            )}
        </>
    );
    
    const ResultText = () => (
        <>
            {textResults.map(text => 
                <div style={{padding:'10px'}} key={useId()} dangerouslySetInnerHTML={{ __html: text }} />
            )}
        </>
    );

    const ResultTar = () => (
        <Button>
            <a href={resultTarFile} download> Download Results <FaFileDownload /> </a>
        </Button>
    );

    const JobInfo = () => {
        if (requestId === null && requestIdsList === null) {
            return <LdsSpinner/>
        } else if (!requestIdsList.length) {
            return <h3 style={{textAlign: 'center'}}>No jobs found</h3>
        } else {
            return (
                <div style={{width: '50%', float: 'left'}}>
                    <b> Select Job to View: </b>

                    <TextField
                        className={classNames(classes.margin, classes.textField)}
                        select
                        style={{maxWidth: '445px'}}
                        name="requestId"
                        value={requestId}
                        onClick={() => getRequests()}
                        onChange={handleInputChange}>

                        {requestIdsList.map(reqId => (
                            <MenuItem key={useId()} value={reqId.requestId}>
                                {reqId.description}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        className={classNames(classes.margin, classes.textField)}
                        variant="outlined"
                        color="default"
                        onClick={() => deleteRequestData(requestId)}> Delete Project
                    </Button>
                    <DelMsg />
                    <h3> Job Status:  </h3> <p style={{'marginLeft': '20px'}}> { requestStatus }</p>
                    <h3> Job ID:      </h3> <p style={{'marginLeft': '20px'}}> { requestId } </p>
                    <RequestBody />
                </div>
            )
        }
    }

    const DelMsg = () => {
        return (
            delMsg !== ''
            ? <i style={{marginLeft: '15px'}}>{ delMsg }</i>
            : <></>
        )
    }

    const RequestBody = () => {
        if (requestBody !== null && typeof requestBody == 'object') {
            return (
                <div>
                    <Button
                        onClick={() => setOpenRequestBodyTable(openRequestBodyTable => !openRequestBodyTable)}
                        style={{width: '90%'}}
                        variant='outlined'
                    > Request Body </Button>

                <Collapse in={openRequestBodyTable} timeout="auto" unmountOnExit>
                        <Table style={{width: '90%'}} >
                                <TableHead>
                                    <TableRow>
                                        <TableCell className='thin-column' > Variable </TableCell>
                                        <TableCell className='thick-column' > Value </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(requestBody).map(key => (
                                        <TableRow key={key} >
                                            <TableCell className='thin-column'> {key} </TableCell>
                                            <TableCell className='thick-column' > {JSON.stringify(requestBody[key], null, 4)} </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                        </Table>
                    </Collapse>
                </div>
            )
        } else {
            return <LdsSpinner/>
        }
    }

    return (
        <div >
            <div style={{textAlign: 'center', width: '100%'}}>
                <h1> Job Results </h1>
            </div>
            <JobInfo />
            <div style={{width:'50%', float: 'right'}}>
                <div>
                    {Boolean(requestId) && <h3>Info:</h3>}
                    <ResultText />
                    {resultTarFile && <ResultTar />}
                </div>
                <ResultImages />
            </div>
        </div>
    )
}

export default Results;